import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchPaises, removePais } from '../../reducers/pais_slice';
import PaisForm from './PaisForm';
import Table from 'react-bootstrap/Table';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import Button from 'react-bootstrap/Button';
import CustomPrintModal from '../UI/CustomPrintModal';

const PaisList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const paises = useSelector(state => state.pais.paises || []);
  const [showModal, setShowModal] = useState(false);
  const [currentPais, setCurrentPais] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });

  useEffect(() => {
    dispatch(fetchPaises());
  }, [dispatch]);

  // Ordenar países por nombre
  const paisesOrdenados = useMemo(() => {
    return [...paises].sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [paises]);

  const handleDelete = id => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "¡No podrás revertir esto!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#464646',
      confirmButtonText: 'Sí, eliminarlo!',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(removePais(id)).unwrap().then(() => {
          Swal.fire('¡Eliminado!', 'El país ha sido eliminado.', 'success');
        }).catch((error) => {
          Swal.fire('Error', error.message || 'No se pudo eliminar el país.', 'error');
        });
      }
    });
  };

  const handleEdit = pais => {
    setCurrentPais(pais);
    setShowModal(true);
  };

  const handleCreate = () => {
    setCurrentPais(null);
    setShowModal(true);
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      paisesOrdenados.map((pais, index) => ({
        Número: index + 1,
        Nombre: pais.nombre,
        "Gentilicio Masculino": pais.gentilicio_masc,
        "Gentilicio Femenino": pais.gentilicio_fem,
        Abreviatura: pais.abrev,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Paises");
    XLSX.writeFile(workbook, "paises.xlsx");
  };

  const handleShowPreview = () => {
    const content = {
      head: [['Número', 'Nombre', 'Gentilicio Masculino', 'Gentilicio Femenino', 'Abreviatura']],
      body: paisesOrdenados.map((pais, index) => [
        index + 1,
        pais.nombre,
        pais.gentilicio_masc,
        pais.gentilicio_fem,
        pais.abrev,
      ])
    };
    setPrintContent(content);
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
  };

  const handleBack = () => {
    navigate('/admin'); // Ajusta la ruta según corresponda
  };

  return (
    <div className="list-container list0">
      <h1 className="table-title">Lista de Países</h1>
      <div className="list-container-header d-flex justify-content-between">
        {/* Botón Volver */}
        <div className="left-buttons">
          <button title="Volver" onClick={handleBack} className="icon-button btn-transparent">
            <i className="bi bi-arrow-left"></i>
          </button>
        </div>
        {/* Botones de acción */}
        <div className="right-buttons d-flex gap-2">
          <Button variant="outline-primary" onClick={handleCreate}>
            <i className="bi bi-plus-circle"></i> Nuevo País
          </Button>
          <Button variant="outline-secondary" onClick={handleShowPreview}>
            <i className="bi bi-printer"></i> Imprimir
          </Button>
          <Button variant="outline-success" onDoubleClick={handleExportExcel}>
            <i className="bi bi-file-spreadsheet"></i> Exportar a Excel
          </Button>
        </div>
      </div>
      <div className="table-container">
        <Table bordered hover size='sm'>
          <thead className='text-center align-middle'>
            <tr>
              <th>#</th>
              <th>Nombre</th>
              <th>Gentilicio Masculino</th>
              <th>Gentilicio Femenino</th>
              <th>Abreviatura</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paisesOrdenados.map((pais, index) => (
              <tr key={pais.id}>
                <td>{index + 1}</td>
                <td>{pais.nombre}</td>
                <td>{pais.gentilicio_masc}</td>
                <td>{pais.gentilicio_fem}</td>
                <td>{pais.abrev}</td>
                <td>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    title="Editar" 
                    onClick={() => handleEdit(pais)}
                  >
                    <i className="bi bi-pencil"></i>
                  </Button>{' '}
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    title="Eliminar" 
                    onClick={() => handleDelete(pais.id)}
                  >
                    <i className="bi bi-trash"></i>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {paisesOrdenados.length === 0 && (
          <p className="text-center mt-4">No hay países disponibles.</p>
        )}
      </div>
      <PaisForm
        currentPais={currentPais}
        showModal={showModal}
        handleClose={() => setShowModal(false)}
      />
      <CustomPrintModal
        show={showPreviewModal}
        handleClose={handleClosePreview}
        title1="LISTA DE PAÍSES"
        title2=""
        content={printContent}
        copia={false}
      />
    </div>
  );
};

export default PaisList;
